/**
 * Human-readable currency labels and local/USD helpers.
 * Mirrors BFF `domain/utils/currencyLabel.ts` (ARS / UYU / USD).
 * Currency SoT — do not gate on `isUruguayEnabled`.
 */

export function currencyDisplayLabel(currencyCode?: string | null): string {
  const code = (currencyCode ?? '').toUpperCase();
  switch (code) {
    case 'ARS':
      return 'Pesos Argentinos';
    case 'UYU':
      return 'Pesos Uruguayos';
    case 'USD':
      return 'Dolares';
    default:
      return currencyCode ?? '';
  }
}

export function isLocalCurrencyCode(currencyCode?: string | null): boolean {
  const code = (currencyCode ?? '').toUpperCase();
  return code === 'ARS' || code === 'UYU';
}

/**
 * Resolve ISO code from monetization labels, symbols, or codes.
 * "Pesos Uruguayos" / UYU → UYU; generic "peso" / ARS → ARS; dollar variants → USD.
 */
export function resolveCurrencyCode(
  currency?: string | null,
  fallback = 'ARS'
): string {
  if (!currency?.trim()) return fallback;
  const raw = currency.trim();
  const upper = raw.toUpperCase();
  if (upper === 'ARS' || upper === 'UYU' || upper === 'USD') return upper;

  const lower = raw.toLowerCase();
  if (lower.includes('uruguay') || lower === 'uyu') return 'UYU';
  if (
    lower.includes('dolar') ||
    lower.includes('dólar') ||
    lower === 'usd' ||
    lower.includes('u$')
  ) {
    return 'USD';
  }
  if (lower.includes('peso') || lower === 'ars' || lower === '$') return 'ARS';
  return upper;
}

export type LocalCurrencyCode = 'ARS' | 'UYU';

export function currencyNumberFormatter(
  currencyCode: LocalCurrencyCode | 'USD'
): Intl.NumberFormat {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function isoCodeForGuaranteeCurrency(
  input: {
    currencyId?: number | null;
    currencyCode?: string | null;
    currencyLabel?: string | null;
  },
  localCurrencyCode: LocalCurrencyCode
): LocalCurrencyCode | 'USD' {
  const fromApi = resolveCurrencyCode(
    input.currencyCode || input.currencyLabel,
    ''
  );
  if (fromApi === 'ARS' || fromApi === 'UYU' || fromApi === 'USD') {
    return fromApi;
  }
  return localCurrencyCode;
}

/** Local ($ ARS/UYU) vs USD display symbol used in monetization banners. */
export function currencySymbol(currencyCode?: string | null): string {
  return isLocalCurrencyCode(currencyCode) ? '$' : 'U$D';
}
